import type { BookmarkType } from "@volgo/core/contract";
import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router";
import { useFavorites } from "../personal/favorites";
import { useSession } from "../session";
import { Button } from "../ui/Button";
import { ConfirmDialog } from "../ui/Dialog";
import { Cover, coverOf, GalleryCard } from "../ui/GalleryCard";
import { useGallerySource } from "../ui/GallerySource";
import { PagedFooter } from "../ui/PagedFooter";
import { Segmented } from "../ui/Segmented";
import { ErrorNote, Loading } from "../ui/States";
import { useLoad, usePaged } from "../ui/useLoad";

export function Visibility({ value }: { value: "private" | "public" }) {
  return (
    <span className={value === "public" ? "bookmark-public" : "note"}>
      {value === "public" ? "🌐 공개" : "🔒 프라이빗"}
    </span>
  );
}
export function BookmarkEditor({
  bookmark,
  onDone,
  onCancel,
}: {
  bookmark?: BookmarkType;
  onDone: (b: BookmarkType) => void;
  onCancel: () => void;
}) {
  const { client } = useSession();
  const [name, setName] = useState(bookmark?.name ?? ""),
    [description, setDescription] = useState(bookmark?.description ?? ""),
    [visibility, setVisibility] = useState<"private" | "public">(bookmark?.visibility ?? "private"),
    [busy, setBusy] = useState(false),
    [error, setError] = useState<unknown>(null);
  return (
    <form
      className="bookmark-editor"
      onSubmit={(e) => {
        e.preventDefault();
        if (busy || !name.trim()) return;
        setBusy(true);
        setError(null);
        const body = { name: name.trim(), description, visibility };
        (bookmark
          ? client.call("personal.updateBookmark", { params: { id: bookmark.id }, body })
          : client.call("personal.createBookmark", { body })
        )
          .then(onDone, (e) => setError(e))
          .finally(() => setBusy(false));
      }}
    >
      <label>
        이름
        <input required maxLength={60} value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label>
        설명
        <textarea maxLength={300} value={description} onChange={(e) => setDescription(e.target.value)} />
      </label>
      <label>
        공개 범위
        <select value={visibility} onChange={(e) => setVisibility(e.target.value as "private" | "public")}>
          <option value="private">🔒 프라이빗</option>
          <option value="public">🌐 공개</option>
        </select>
      </label>
      <p className="note">
        공개 북마크는 로그인한 Volgo 회원에게 보입니다. 이미 전달된 정보는 프라이빗으로 바꿔도 회수할 수
        없습니다.
      </p>
      {error !== null && <ErrorNote error={error} />}
      <div className="acts">
        <Button type="submit" disabled={busy || !name.trim()}>
          {busy ? "저장 중" : "저장"}
        </Button>
        <Button onClick={onCancel} disabled={busy}>
          취소
        </Button>
      </div>
    </form>
  );
}
export function BookmarksPage() {
  const { client } = useSession(),
    favorites = useFavorites();
  const [scope, setScope] = useState<"all" | "mine" | "public">("all"),
    [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const list = usePaged(
    (cursor) =>
      client.call("personal.listBookmarks", { query: { scope, limit: 80, ...(cursor ? { cursor } : {}) } }),
    `${scope}:${favorites.revision}`,
  );
  return (
    <div className="page">
      <div className="page-head">
        <h1 tabIndex={-1}>북마크</h1>
        <Button onClick={() => setCreating((v) => !v)}>북마크 만들기</Button>
      </div>
      <Segmented
        legend="북마크 범위"
        options={[
          { value: "all", label: "전체" },
          { value: "mine", label: "내 북마크" },
          { value: "public", label: "공개" },
        ]}
        isOn={(v) => v === scope}
        onPick={setScope}
      />
      {creating && (
        <BookmarkEditor
          onCancel={() => setCreating(false)}
          onDone={(b) => {
            favorites.bookmarksChanged();
            navigate(`/bookmarks/${b.id}`);
          }}
        />
      )}
      <ul className="bookmark-rows">
        {list.items.map((b) => (
          <li key={b.id}>
            <Link className="bookmark-row" to={`/bookmarks/${b.id}`}>
              <div className="bookmark-previews" aria-hidden="true">
                {b.previews.map((g) => (
                  <div className="cv" key={g.gid}>
                    {coverOf(g) !== null && <Cover src={coverOf(g)!} veiled={g.policy.level === "warn"} />}
                  </div>
                ))}
              </div>
              <div className="row-main">
                <h2>{b.name}</h2>
                <p className="note">
                  {b.itemCount}개 · {b.ownerName}
                  {b.isSaved ? " · 저장함" : ""}
                </p>
              </div>
              <Visibility value={b.visibility} />
            </Link>
          </li>
        ))}
      </ul>
      <PagedFooter list={list} emptyTitle="북마크가 없습니다" />
    </div>
  );
}
export function LegacyFavorites() {
  const { client } = useSession();
  const list = usePaged(
    (cursor) =>
      client.call("personal.listBookmarks", {
        query: { scope: "mine", limit: 100, ...(cursor ? { cursor } : {}) },
      }),
    "default",
  );
  const item = list.items.find((b) => b.isOwner && b.isDefault);
  useEffect(() => {
    if (!item && list.hasMore) list.loadMore();
  }, [item, list.hasMore, list.loadMore]);
  if (item) return <Navigate to={`/bookmarks/${item.id}`} replace />;
  if (list.status === "error") return <ErrorNote error={list.error} onRetry={list.reload} />;
  return <Loading />;
}
export function BookmarkPage() {
  const id = useParams().id!,
    { client } = useSession(),
    favorites = useFavorites(),
    navigate = useNavigate();
  const [editing, setEditing] = useState(false),
    [asking, setAsking] = useState(false),
    [error, setError] = useState<unknown>(null);
  const info = useLoad(() => client.call("personal.getBookmark", { params: { id } }), id);
  const { set: setInfo } = info;
  useEffect(() => {
    if (favorites.revision === 0) return;
    let alive = true;
    client.call("personal.getBookmark", { params: { id } }).then(
      (b) => {
        if (alive) setInfo(b);
      },
      (e) => {
        if (alive) setError(e);
      },
    );
    return () => {
      alive = false;
    };
  }, [client, id, favorites.revision, setInfo]);
  const list = usePaged(
    (cursor) =>
      client.call("personal.bookmarkItems", {
        params: { id },
        query: { limit: 80, ...(cursor ? { cursor } : {}) },
      }),
    id,
  );
  useGallerySource(list.items);
  const gids = useMemo(() => list.items.map((g) => g.gid), [list.items]);
  const { setItems } = list;
  useEffect(() => {
    if (!info.data?.isOwner || favorites.revision === 0 || !gids.length) return;
    let alive = true;
    const run = async () => {
      const keep = new Set<number>();
      for (let i = 0; i < gids.length; i += 100) {
        const r = await client.call("personal.bookmarkMemberships", {
          body: { gids: gids.slice(i, i + 100) },
        });
        for (const item of r.items) if (item.bookmarkIds.includes(id)) keep.add(item.gid);
      }
      if (alive)
        setItems((items) =>
          items.every((g) => keep.has(g.gid)) ? items : items.filter((g) => keep.has(g.gid)),
        );
    };
    run().catch(setError);
    return () => {
      alive = false;
    };
  }, [client, id, favorites.revision, info.data?.isOwner, gids, setItems]);
  async function remove(gid: number) {
    try {
      await client.call("personal.changeBookmarkMemberships", {
        params: { gid },
        body: { addIds: [], removeIds: [id] },
      });
      list.setItems((items) => items.filter((g) => g.gid !== gid));
      favorites.changed(gid);
    } catch (e) {
      setError(e);
    }
  }
  if (info.status === "loading")
    return (
      <div className="page">
        <Loading />
      </div>
    );
  if (info.status === "error")
    return (
      <div className="page">
        <ErrorNote error={info.error} onRetry={info.reload} />
      </div>
    );
  const b = info.data;
  return (
    <div className="page">
      <Link to="/bookmarks">← 북마크</Link>
      <div className="page-head">
        <h1 tabIndex={-1}>{b.name}</h1>
        <Visibility value={b.visibility} />
      </div>
      <p className="note">
        {b.itemCount}개 · {b.ownerName}
      </p>
      {b.description && <p>{b.description}</p>}
      <div className="acts">
        {b.isOwner ? (
          <>
            <Button onClick={() => setEditing((v) => !v)}>편집</Button>
            {!b.isDefault && <Button onClick={() => setAsking(true)}>북마크 삭제</Button>}
          </>
        ) : (
          <Button
            onClick={() => {
              client
                .call(b.isSaved ? "personal.unsaveBookmark" : "personal.saveBookmark", { params: { id } })
                .then(() => {
                  favorites.bookmarksChanged();
                  info.reload();
                }, setError);
            }}
          >
            {b.isSaved ? "내 목록에서 해제" : "내 목록에 저장"}
          </Button>
        )}
      </div>
      {editing && (
        <BookmarkEditor
          bookmark={b}
          onCancel={() => setEditing(false)}
          onDone={() => {
            setEditing(false);
            favorites.bookmarksChanged();
            info.reload();
          }}
        />
      )}
      {error !== null && <ErrorNote error={error} />}
      <section className="grid" aria-label="북마크 작품">
        {list.items.map((g) => (
          <GalleryCard key={g.gid} card={g} to={`/g/${g.gid}`}>
            {b.isOwner && (
              <p className="card-extra">
                <button type="button" onClick={() => remove(g.gid)}>
                  북마크에서 빼기
                </button>
              </p>
            )}
          </GalleryCard>
        ))}
      </section>
      <PagedFooter list={list} emptyTitle="북마크가 비어 있습니다" moreLabel="↓ 80개 더 보기">
        <p>작품 상세에서 이 북마크를 선택해 저장하세요.</p>
      </PagedFooter>
      {asking && (
        <ConfirmDialog
          title="북마크를 삭제할까요?"
          confirmLabel="삭제"
          onCancel={() => setAsking(false)}
          onConfirm={() => {
            setAsking(false);
            client.call("personal.deleteBookmark", { params: { id } }).then(() => {
              favorites.bookmarksChanged();
              navigate("/bookmarks");
            }, setError);
          }}
        >
          <p>북마크 묶음만 삭제합니다. 작품과 다운로드 파일은 유지됩니다.</p>
        </ConfirmDialog>
      )}
    </div>
  );
}
